import type { IdentityReport } from "./IdentityWorkbench/identityWorkbenchContracts";

export type directorViewIdentityMatch = {
  identityId: string | null;
  label: string;
  confidence: string;
  resolutionStatus: string;
  supportEvidenceCount: number;
  detectionCount: number;
  unknown: boolean;
  breedObservation: IdentityReport["subjects"][number]["breed_observation"];
};

const VIDEO_INSTANCE_WINDOW_US = 500_000;

function identityLabel(report: IdentityReport, identityId: string) {
  const index = [...report.subjects]
    .sort((left, right) =>
      left.echo_identity_id.localeCompare(right.echo_identity_id),
    )
    .findIndex((subject) => subject.echo_identity_id === identityId);
  return index >= 0 ? `Dog ${index + 1}` : "Dog";
}

function instanceIsActive(
  instance: IdentityReport["instances"][number],
  sourceTimeUs: number | null,
) {
  if (sourceTimeUs == null) return true;
  if (instance.time_range_us) {
    return (
      sourceTimeUs >= instance.time_range_us[0] &&
      sourceTimeUs <= instance.time_range_us[1]
    );
  }
  return (
    instance.timestamp_us != null &&
    Math.abs(instance.timestamp_us - sourceTimeUs) <= VIDEO_INSTANCE_WINDOW_US
  );
}

/**
 * Correlate the authorized identity report with the original media currently
 * shown by Director View. Opaque source-media IDs are the only join key; filenames
 * and storage locators are deliberately never used.
 */
export function directorViewIdentityMatches(
  report: IdentityReport | null,
  sourceMediaId: string | null,
  sourceTimeUs: number | null,
): directorViewIdentityMatch[] {
  if (!report || !sourceMediaId) return [];

  const activeTrackletIds = new Set(
    sourceTimeUs == null
      ? report.tracklets
          .filter((tracklet) => tracklet.source_media_id === sourceMediaId)
          .map((tracklet) => tracklet.tracklet_id)
      : report.tracklets
          .filter(
            (tracklet) =>
              tracklet.source_media_id === sourceMediaId &&
              sourceTimeUs >= tracklet.start_us &&
              sourceTimeUs <= tracklet.end_us,
          )
          .map((tracklet) => tracklet.tracklet_id),
  );
  const activeInstances = report.instances.filter(
    (instance) =>
      instance.source_media_id === sourceMediaId &&
      (sourceTimeUs == null ||
        (instance.tracklet_id != null &&
          activeTrackletIds.has(instance.tracklet_id)) ||
        instanceIsActive(instance, sourceTimeUs)),
  );

  const identityIds = new Set<string>();
  for (const assignment of report.assignments) {
    if (!assignment.echo_identity_id) continue;
    const hasActiveTracklet = assignment.tracklet_ids.some((trackletId) =>
      activeTrackletIds.has(trackletId),
    );
    const hasActiveInstance = assignment.instance_ids.some((instanceId) =>
      activeInstances.some((instance) => instance.instance_id === instanceId),
    );
    if (hasActiveTracklet || hasActiveInstance) {
      identityIds.add(assignment.echo_identity_id);
    }
  }
  for (const instance of activeInstances) {
    if (instance.assigned_echo_identity_id) {
      identityIds.add(instance.assigned_echo_identity_id);
    }
  }
  // A still-image reference remains valid evidence even when the bounded
  // report's instance list was truncated.
  if (sourceTimeUs == null) {
    for (const subject of report.subjects) {
      if (
        subject.references.some(
          (reference) => reference.source_media_id === sourceMediaId,
        )
      ) {
        identityIds.add(subject.echo_identity_id);
      }
    }
  }

  const matches: directorViewIdentityMatch[] = [];
  for (const identityId of identityIds) {
    const subject = report.subjects.find(
      (candidate) => candidate.echo_identity_id === identityId,
    );
    if (!subject) continue;
    const assignments = report.assignments.filter(
      (assignment) => assignment.echo_identity_id === identityId,
    );
    matches.push({
      identityId,
      label: identityLabel(report, identityId),
      confidence: subject.confidence,
      resolutionStatus: subject.resolution_status,
      supportEvidenceCount: Math.max(
        subject.cluster_support_count,
        ...assignments.map((assignment) => assignment.support_evidence_count),
      ),
      detectionCount: activeInstances.filter(
        (instance) => instance.assigned_echo_identity_id === identityId,
      ).length,
      unknown: false,
      breedObservation: subject.breed_observation,
    });
  }
  matches.sort((left, right) => left.label.localeCompare(right.label));

  const unknownCount = activeInstances.filter(
    (instance) => instance.assigned_echo_identity_id == null,
  ).length;
  if (unknownCount > 0) {
    matches.push({
      identityId: null,
      label: "Unknown dog",
      confidence: "unknown",
      resolutionStatus: "abstained",
      supportEvidenceCount: 0,
      detectionCount: unknownCount,
      unknown: true,
      breedObservation: null,
    });
  }
  return matches;
}
